// --- Nested Interfaces ---

export interface AssignedMemberDetailRetrive {
  id: string;
  created_at: string; // Date string (e.g., "2025-10-08")
  updated_at: string; // Date string
  created_by_user: string;
  updated_by_user: string;
  user: string; // UUID
  user_name: string;
  user_email: string;
  sales_order_type: "product" | "service";
  created_by: string; // UUID
  updated_by: string; // UUID
  is_active: boolean;
  is_deleted: boolean;
  assigned_date: string; // Date string (e.g., "2025-10-08")
  product_sales_order: string | null; // UUID or null
  service_sales_order: string | null; // UUID or null
}

export interface Address {
  address: string;
  city: string;
  state: string;
  country: string;
  pin_code: string;
  phone_number?: string;
}

export interface ShippingAddress {
  id: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pin_code: string;
  phone_number?: string;
}

interface ItemDetail {
  id: string;
  created_at: string; // Date string
  updated_at: string; // Date string
  created_by_user: string;
  updated_by_user: string;
  item_name: string;
  item_type: string; // e.g., "products"
  category_name: string;
  sub_category_name: string;
  make_name: string;
  model_name: string;
  sales_order_type: "product" | "service";
  created_by: string; // UUID
  updated_by: string; // UUID
  is_active: boolean;
  is_deleted: boolean;
  installation: string; // e.g., "Applicable"
  installation_date: string; // Date string (e.g., "2025-10-15")
  inspection: string; // e.g., "Applicable"
  inspection_date: string; // Date string (e.g., "2025-10-20")
  quantity: number;
  unit_price: string; // Numeric string (e.g., "5000.00")
  discount_percent: string; // Numeric string
  discount_amount: string; // Numeric string
  net_value: string; // Numeric string
  gst_percent: string; // Numeric string
  cgst_percent: string; // Numeric string
  igst_percent: string; // Numeric string
  sgst_percent: string; // Numeric string
  gst_amount: string; // Numeric string
  total_amount: string; // Numeric string
  is_serviceable: boolean;
  product_sales_order: string | null; // UUID or null
  service_sales_order: string | null; // UUID or null
  item: string; // UUID
  category: string; // UUID
  sub_category: string; // UUID
  make: string; // UUID
  model: string; // UUID
}

interface UploadDocument {
  file_url: string;
  file_name: string;
}

interface PBGDetail {
  id: string;
  created_at: string; // Date string
  updated_at: string; // Date string
  created_by_user: string;
  updated_by_user: string;
  sales_order_type: "product" | "service";
  created_by: string; // UUID
  updated_by: string; // UUID
  is_active: boolean;
  is_deleted: boolean;
  pbg_required: boolean;
  pbg_number: string;
  pbg_date: string; // Date string
  bank_name: string;
  amount: string; // Numeric string
  validity: string; // e.g., "12 months"
  pbg_status: string; // e.g., "Recieved"
  next_follow_up_date?: string; // PBG-specific follow-up date
  // upload_document: UploadDocument[];
  upload_document: (string | File)[]; // Array of file URLs or File objects
  product_sales_order: string | null; // UUID or null
  service_sales_order: string | null; // UUID or null
}

interface Attachment {
  id: string;
  created_at: string; // Date string
  updated_at: string; // Date string
  created_by_user: string;
  updated_by_user: string;
  created_by: string; // UUID
  updated_by: string; // UUID
  is_active: boolean;
  is_deleted: boolean;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  product_sales_order: string | null; // UUID or null
  service_sales_order: string | null; // UUID or null
}

// interface AgentDetail {
//   agent_name: string;
//   agent_email: string;
//   agent_phone_no: string;
//   agent_department: string;
//   agent_designation: string;
// }

// NOTE: The 'contact_details' and 'billing_address' fields are JSON strings,
// not actual JSON objects in the provided sample.
// You should handle this by parsing them into proper objects in your application logic.
// However, the interface should reflect their current string type.

// If you know the structure of the JSON string content, you can define them:

interface ContactDetail {
  id?: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone_no: string;
  contact_person_department: string;
  contact_person_designation: string;
  name?: string;
  email?: string;
}
interface BillingAddress {
  city: string;
  state: string;
  address: string;
  country: string;
  pin_code: string;
  phone_number: string;
}

export interface Company {
  id: string;
  company_name: string;
  industry_name?: string;
   branch_name?: string | null;
  vendor_code?: string | null;
  contact_details?: ContactDetail[];
   customer_po_number?: string;
  customer_po_date?: string;
  customer_po_document_url?: string | null;
  [key: string]: any;
}

export interface RetentionDetailsValue {
  retention_applicable: boolean;
  retentionType: string;
  percentage: number | "";
  retentionAmount: number | "";
  followUpDate: string;
}
// --- Main Interface ---

type UserOption = {
  user_id: string;
  full_name?: string;
  username?: string;
};

export interface SalesOrderDataRetrive {
  id: string; // UUID
  created_at: string; // Date string
  updated_at: string; // Date string
  created_by_user: string;
  updated_by_user: string;
  contact_person_phone_no: string;
  members: any[];
  // Nested Arrays
  assigned_member_details: AssignedMemberDetailRetrive[];
  emd_applicable: boolean;
  mode_of_emd: string;
  emd: string;
  emd_amount: string;
  item_details: ItemDetail[];
  pbg_details: PBGDetail;
  attachments:(string | File)[];
  company: Company;
  // Main Details
  company_name: string;
  company_email: string;
  // JSON Strings (should be parsed on use)
  contact: ContactDetail[]; // JSON String of ContactDetail[]
  organization_billing_address: {};
  billing_address: Address;
  shipping_address: ShippingAddress[];
  quotation_reference: string | null;
  quotation_number: string;
  enquiry_reference: string;
  enquiry_number: string;
  reference_number: string;
  bill_to_address: Address | null;
  bill_from_address: Address | null;
  bill_to: string | null;
  bill_from: string | null;
  created_by: string; // UUID
  updated_by: string; // UUID
  is_active: boolean;
  is_deleted: boolean;
  sales_order_stage: string; // e.g., "orders_log"
  sales_order_number: string;
  sales_order_date: string; // Date string
  valid_until_date: string; // Date string
  initiated_by: string;
  industry: string;
  contactPerson: {};
  contactPersonArray?: ContactDetail[]; // ✅ type correctly for multiple selection
  
  // Contact Person Details (Top Level)
  contact_person_name: string;
  contact_person_designation: string;
  contact_person_email: string;

  agent_name: string ;

  // Numeric Strings
  subtotal_before_discount: string;
  total_item_discounts: string;
  net_taxable_value: string;
  total_gst: string;
  total_amount: string;

  terms_and_conditions: string | Array<{ type: string; options: string; optionId?: string | number }>;
  notes: string;
  status: string; // e.g., "In Progress"

  // Foreign Keys / References (UUIDs)
  quotation: string;
  enquiry: string;

  /** ★★★★★ NEW FIELDS ADDED ↓↓↓ ★★★★★ */
  customer_po_number: string; // PO Number entered by customer
  customer_po_date: string; // Date string (YYYY-MM-DD)
  customer_po_document_url: (string | File)[];  // array of files/urls

  currency_applicable?: boolean; // toggle for Currency & Exchange section
  currency_code: string; // e.g., "INR"
  currency_symbol: string; // e.g., "₹", "$", "€"
  exchange_rate: string; // numeric string or empty
  composition_gst: boolean; // true/false
  select_composition_gst: string; // e.g., "5%", "12%", or empty

  payment_terms: string;
  payment_terms_amount: string | number;
  advance_payment: string;
  pending_payment: string;
  next_credit_schedule: string;
  payment_reminder: string;

   inspection_applicable?: boolean; // toggle for Inspection & Installation section
   inspection?: string;           // e.g., "Applicable" or "Not Applicable"
  inspection_date?: string;     // required only if inspection === "Applicable"
  installation?: string;         // e.g., "Applicable" or "Not Applicable"
  installation_date?: string;   // required only if installation === "Applicable"

  technicians?: UserOption[];

  retention_details: RetentionDetailsValue;

  // Schedule Follow-up
  next_follow_up_date: string;
  follow_up_remarks: string;

  // Tags for Sales Order (array of strings)
  tags?: string[];
}

const initialAssignedMemberDetail: AssignedMemberDetailRetrive = {
  id: "",
  created_at: "",
  updated_at: "",
  created_by_user: "",
  updated_by_user: "",
  user: "",
  user_name: "",
  user_email: "",
  sales_order_type: "product" as const, // Use 'as const' for literal types
  created_by: "",
  updated_by: "",
  is_active: true,
  is_deleted: false,
  assigned_date: "",
  product_sales_order: null,
  service_sales_order: null,
};

const initialItemDetail: ItemDetail = {
  id: "",
  created_at: "",
  updated_at: "",
  created_by_user: "",
  updated_by_user: "",
  item_name: "",
  item_type: "",
  category_name: "",
  sub_category_name: "",
  make_name: "",
  model_name: "",
  sales_order_type: "product" as const,
  created_by: "",
  updated_by: "",
  is_active: true,
  is_deleted: false,
  installation: "",
  installation_date: "",
  inspection: "",
  inspection_date: "",
  quantity: 0,
  unit_price: "0.00",
  discount_percent: "0.00",
  discount_amount: "0.00",
  net_value: "0.00",
  gst_percent: "0.00",
  cgst_percent: "0.00",
  igst_percent: "0.00",
  sgst_percent: "0.00",
  gst_amount: "0.00",
  total_amount: "0.00",
  is_serviceable: false,
  product_sales_order: null,
  service_sales_order: null,
  item: "",
  category: "",
  sub_category: "",
  make: "",
  model: "",
};

const initialPBGDetail: PBGDetail = {
  id: "",
  created_at: "",
  updated_at: "",
  created_by_user: "",
  updated_by_user: "",
  sales_order_type: "product" as const,
  created_by: "",
  updated_by: "",
  is_active: true,
  is_deleted: false,
  pbg_required: false,
  pbg_number: "",
  pbg_date: "",
  bank_name: "",
  amount: "0.00",
  validity: "",
  pbg_status: "",
  next_follow_up_date: "",
  upload_document: [], // Array of { file_url: string, file_name: string }
  product_sales_order: null,
  service_sales_order: null,
};

const initialAttachment: Attachment = {
  id: "",
  created_at: "",
  updated_at: "",
  created_by_user: "",
  updated_by_user: "",
  created_by: "",
  updated_by: "",
  is_active: true,
  is_deleted: false,
  file_name: "",
  file_path: "",
  file_size: 0,
  file_type: "",
  product_sales_order: null,
  service_sales_order: null,
};

// const initialAgentDetail: AgentDetail = {
//   agent_name: "",
//   agent_email: "",
//   agent_phone_no: "",
//   agent_department: "",
//   agent_designation: "",
// };

// --- Main Initial Sales Order Data ---

export const initialSalesOrderData: SalesOrderDataRetrive = {
  id: "",
  created_at: "",
  updated_at: "",
  created_by_user: "",
  updated_by_user: "",
  contact_person_phone_no: "",
  company_email: "",

  // Nested Arrays initialized as empty arrays
  members: [],
  assigned_member_details: [],
  item_details: [],
  pbg_details: initialPBGDetail,
  attachments: [],

  // EMD related fields
  emd_applicable: false,
  mode_of_emd: "",
  emd: "",
  emd_amount: "",

  company: {
    id: "",
    company_name: "",
    industry_name: "",
    contact: [],
    customer_po_number: "",
    customer_po_date: "",
    customer_po_document_url: null,
  },

  // Main Details
  company_name: "",


  contactPerson: {
    id: "",
    contact_person_name: "",
    contact_person_email: "",
    contact_person_phone_no: "",
    contact_person_designation: "",
    name: "",
    email: "",
  },

  

  contactPersonArray: [], // empty array instead of {}


  billing_address: {
    city: "",
    state: "",
    address: "",
    country: "",
    pin_code: "",
    phone_number: "",
  },

  organization_billing_address: {},

  shipping_address: [],
  bill_to_address: null,
  bill_from_address: null,
  bill_to: null,
  bill_from: null,

  quotation_reference: null,
  quotation_number: "",
  enquiry_reference: "",
  enquiry_number: "",
  reference_number: "",

  created_by: "",
  updated_by: "",

  is_active: true,
  is_deleted: false,
  sales_order_stage: "",
  sales_order_number: "",
  sales_order_date: "",
  valid_until_date: "",
  initiated_by: "",
  industry: "",
  contact: [],

  // Contact Person Details
  contact_person_name: "",
  contact_person_designation: "",
  contact_person_email: "",

  agent_name: "",

  // Numeric Strings
  subtotal_before_discount: "0.00",
  total_item_discounts: "0.00",
  net_taxable_value: "0.00",
  total_gst: "0.00",
  total_amount: "0.00",

  terms_and_conditions: "",
  notes: "",
  status: "",

  // Foreign Keys / References
  quotation: "",
  enquiry: "",

  // ⭐⭐⭐ NEW PO FIELDS ⭐⭐⭐
  customer_po_number: "",
  customer_po_date: "",
  customer_po_document_url: [],

  currency_applicable: false,
  currency_code: "INR", // Default currency,
  currency_symbol: "₹", // Default currency symbol
  exchange_rate: "",
  composition_gst: false,
  select_composition_gst:'',


  payment_terms: "",
  payment_terms_amount: "",
  advance_payment: "",
  pending_payment: "",
  next_credit_schedule: "",
  payment_reminder: "",

  inspection_applicable: false,
   inspection: "",           // e.g., "Applicable" or "Not Applicable"
  inspection_date: "",   // required only if inspection === "Applicable"
  installation: "",         // e.g., "Applicable" or "Not Applicable"
  installation_date: "",   // required only if installation === "Applicable"
  technicians:[],

  retention_details: {
    retention_applicable: false,
    retentionType: "",
    percentage: "",
    retentionAmount: "",
    followUpDate: "",
  },

  next_follow_up_date: "",
  follow_up_remarks: "",
  tags: [],
};

// You can export these nested initial states if you need them for
// adding new items to the form dynamically (e.g., adding a new item detail row).
export {
  initialAssignedMemberDetail,
  initialItemDetail,
  initialPBGDetail,
  initialAttachment,
  // initialAgentDetail,
};
